import { proxy, ref } from 'valtio';
import { createWebsocketManager } from './websocket/WebsocketManager.js';
import { createSimulation } from './websocket/Simulation.js';
import { initialHistory, initialFSStatus } from './Constants/InitialSettings.js';
import { all_access_rights, none_access_rights } from './Constants/constants.js';

import _ from 'lodash';

function stateManager() {
  return {
    controllers: [],
    simulation: null,

    alarms: {
      activeAlarms: [],
      alarmsLog: [],
      unreadAlarms: false,
      openAlarms: false
    },
    messages: {
      messages: [],
      unreadMessages: false
    },

    init(wasm, address, hooks) {
      store.address = address.websocket;
      store.wasm = ref(wasm);
      store.hooks = hooks;
      store.wsManager = ref(createWebsocketManager(wasm));
      store.wsManager.init(store);

      store.profile = JSON.parse(localStorage.getItem('profile') ?? '{}');
      store.editMode = false;
    },

    getControllerById: (id) => store.controllers.find((c) => c.id === id),

    getControllerByMacAddress: function (mac_address) {
      return store.controllers.find((c) => c.mac_address === mac_address);
    },

    getControllerByName: (name) => store.controllers.find((c) => c.name === name),

    addController: function (data, user, access_rights_per_controller) {
      let temp_settings = JSON.parse(localStorage.getItem(`${data.id}_temp_settings`) ?? 'null');
      let controller = {
        ...data,

        settings: {},
        temp_settings: temp_settings ? _.cloneDeep(temp_settings) : {}, // hold settings which are not saved yet
        /* wrap history in `data` is needed to keep valtio's `subscribe`
				   working when the history is replaced. Happens in tabs/Graph.js */
        history: { data: _.cloneDeep(initialHistory) },
        infos: { humidity_graph: [], temp_graph: [] },
        fs_status: _.cloneDeep(initialFSStatus),
        connection: {
          initialized: false,
          requested_all: false,
          request_values_full: false,
          status: 'bad',
          last_received: 0
        },
        skipped_values: {},
        access_rights:
          access_rights_per_controller == 'all'
            ? all_access_rights
            : access_rights_per_controller == 'none'
              ? none_access_rights
              : (access_rights_per_controller[data.mac_address] ?? {})
      };
      // Update project_id
      controller.stable_project_id = controller.project_id;
      if (sessionStorage.getItem(controller.id + '_removed_project_restrictions')) {
        controller.project_id = 0;
      }

      // Update graph show/hide values from stored data.
      for (let [key, value] of Object.entries(data['graph_hidden'] ?? {})) {
        controller.history.data.hidden[key] = value;
      }
      delete controller['graph_hidden'];

      // Backward compatibility for release branches
      if (controller.type === 6000) {
        // support zones?
        controller.zones = {};
        controller.slaves = [];
      }

      if (controller.simulation === true) {
        controller.simulation = createSimulation(store.address, controller, user, store.wasm);
        store.simulation = ref(controller.simulation);
      }

      store.controllers = store.controllers
        .filter((c) => c.id !== controller.id) // remove duplicates
        .concat(controller);
    },

    /*
     * @param parent: reference to controller or to zone
     */
    saveSettings(controller, parent, newSettings, tab_id, transform = null) {
      if (newSettings !== null) {
        if (controller.connection.status !== 'bad') {
          parent.settings[tab_id] = _.cloneDeep(newSettings);
          const settings_data = {};
          settings_data[tab_id] = transform
            ? transform(_.cloneDeep(newSettings))
            : _.cloneDeep(newSettings);

          // multi zones
          let zoneable = controller.type === 6000 || (controller.zones && controller.type === 5155);
          if (zoneable && tab_id !== 'system') {
            settings_data.zone = parent.id;
          }
          let forward = controller.type === 6000; // server forwards without parsing data
          let zoned = controller.zones && controller.type === 5155; // server reads cmds that are zoned or non-zoned as zoned.

          store.wsManager.sendWSMsg(controller, 'update_setpoints', settings_data, forward, zoned);
        }
      }
    },

    toggleCustomComponentVisibility: function (controller, parent, id) {
      if (!store.profile[controller.id]) {
        store.profile[controller.id] = {};
      }
      if (!store.profile[controller.id][parent.id]) {
        store.profile[controller.id][parent.id] = {};
      }

      store.profile[controller.id][parent.id][id] = !(
        store.profile[controller.id][parent.id][id] ?? true
      );
      localStorage.setItem('profile', JSON.stringify(store.profile));
    },

    // return reference to the real store behind useSnapshot().
    // use this for manipulating states directly without a set-function
    store() {
      return store;
    }
  };
}

let store;

export default function getStateManager() {
  if (store == null) store = proxy(stateManager()); // makes proxy re-initializable, used for logout
  return store;
}

export function destroyState() {
  store = null;
}
